<!--
  @component GitPanel — per-project source control (LiquidGit, G4).
  tier:      consumer panel (Code screen, below the editor)
  reads:     useDeploymentIO → createGitIO → workspace.git.* (status/log/branches/pull/commit/show)
  purpose:   the "git feature within a project" — see what changed (working copy vs committed
             HEAD), diff a file, commit + push to the project's Gitea repo, and view history.
             Gitea is the source of truth; per-file diff reuses the code-editor engine's text-diff.
-->
<script lang="ts">
  import { useDeploymentIO } from "../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import {
    createEditorEngine, createGitIO,
    type GitIO, type GitStatus, type GitCommitInfo,
  } from "../../../global/io/nodes/code-editor/code-editor-io.js";

  interface Props { projectId: string; }
  const { projectId }: Props = $props();

  const node = useDeploymentIO();
  // GitIO over the dispatch seam; the engine supplies the pure text-diff.
  const git: GitIO = $derived.by(() => {
    const dispatch = (op: string, p?: unknown): Promise<unknown> => node.dispatch(op, p);
    return createGitIO(dispatch, projectId, createEditorEngine(dispatch, projectId));
  });

  let status = $state<GitStatus | null>(null);
  let commits = $state<readonly GitCommitInfo[]>([]);
  let branch = $state("main");
  let loading = $state(true);
  let busy = $state(false);
  let error = $state<string | null>(null);
  let note = $state<string | null>(null);
  let commitMsg = $state("");

  // Inline per-file diff (committed HEAD vs working copy).
  let diffPath = $state<string | null>(null);
  let diffText = $state("");
  const diffLines = $derived(diffText ? diffText.split("\n") : []);

  const changes = $derived(status?.changes ?? []);
  const clean = $derived(status?.clean ?? true);
  const badge = (s: string): string => (s === "added" ? "A" : s === "deleted" ? "D" : "M");

  async function refresh(): Promise<void> {
    if (!projectId) return;
    loading = true; error = null;
    // The deployment WS transport connects a beat after mount; on a fresh load / deep-link the
    // first ops can land before it's OPEN ("transport not connected"). Retry briefly so the panel
    // populates on its own instead of looking empty until the user hits Refresh.
    for (let attempt = 0; attempt < 6; attempt++) {
      try {
        const [s, l] = await Promise.all([git.status(), git.log()]);
        status = s; branch = s.branch; commits = l.commits ?? [];
        loading = false; return;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (/transport not connected/i.test(msg) && attempt < 5) {
          await new Promise((r) => setTimeout(r, 500));
          continue;
        }
        error = msg; loading = false; return;
      }
    }
    loading = false;
  }
  $effect(() => { void refresh(); });

  async function pull(): Promise<void> {
    busy = true; note = null; error = null;
    try {
      const r = await git.pull();
      note = r.files > 0 ? `Checked out ${r.files} file(s) from ${r.branch}` : "Repo has no commits yet";
      await refresh();
    } catch (e) { error = e instanceof Error ? e.message : String(e); }
    finally { busy = false; }
  }

  async function viewDiff(path: string): Promise<void> {
    if (diffPath === path) { diffPath = null; diffText = ""; return; }
    diffPath = path; diffText = "";
    try { diffText = await git.diff(path); } catch (e) { error = e instanceof Error ? e.message : String(e); }
  }

  async function commit(): Promise<void> {
    if (busy || clean) return;
    busy = true; note = null; error = null;
    try {
      const r = await git.commit(commitMsg.trim() || "Update from Liquidefy editor");
      note = r.error ?? r.note ?? "committed";
      commitMsg = ""; diffPath = null; diffText = "";
      await refresh();
    } catch (e) { error = e instanceof Error ? e.message : String(e); }
    finally { busy = false; }
  }

  const shortDate = (iso: string): string => (iso ? iso.slice(0, 10) : "");
  const firstLine = (m: string): string => (m ? (m.split("\n")[0] ?? "") : "");
</script>

<div class="scm">
  <header class="head">
    <h2>Source control</h2>
    <span class="branch" title="current branch">⎇ {branch}</span>
    <div class="spacer"></div>
    <button type="button" class="ghost" disabled={busy} onclick={() => void pull()} title="Load the committed HEAD into your workspace">Checkout HEAD</button>
    <button type="button" class="ghost" disabled={loading} onclick={() => void refresh()}>Refresh</button>
  </header>

  {#if error}<p class="err">{error}</p>{/if}

  <!-- Changes -->
  <section class="block">
    <div class="block-head">
      <span>Changes</span>
      {#if !loading}<span class="count">{changes.length}</span>{/if}
    </div>
    {#if loading}
      <p class="muted">Loading…</p>
    {:else if clean}
      <p class="muted">Nothing to commit — working copy matches <code>{branch}</code>.</p>
    {:else}
      <ul class="changes">
        {#each changes as c (c.path)}
          <li>
            <button type="button" class="chg" class:on={diffPath === c.path} onclick={() => void viewDiff(c.path)}>
              <span class="badge {c.status}">{badge(c.status)}</span>
              <span class="path">{c.path}</span>
            </button>
          </li>
          {#if diffPath === c.path}
            <li class="diffrow">
              {#if diffText}
                <div class="diff">{#each diffLines as l, i (i)}<span
                  class="dl"
                  class:add={l.startsWith("+") && !l.startsWith("+++")}
                  class:del={l.startsWith("-") && !l.startsWith("---")}
                  class:hunk={l.startsWith("@@")}
                  class:meta={l.startsWith("+++") || l.startsWith("---")}
                >{l || " "}</span>{/each}</div>
              {:else}
                <p class="muted small">No textual diff (binary, or identical).</p>
              {/if}
            </li>
          {/if}
        {/each}
      </ul>
    {/if}
  </section>

  <!-- Commit -->
  <div class="commit">
    <input type="text" placeholder="commit message" bind:value={commitMsg} disabled={clean} />
    <button type="button" class="commit-btn" disabled={busy || clean} onclick={() => void commit()}>{busy ? "Committing…" : "Commit & push"}</button>
  </div>
  {#if note}<p class="note">{note}</p>{/if}

  <!-- History -->
  <section class="block">
    <div class="block-head"><span>History</span></div>
    {#if commits.length === 0}
      <p class="muted">No commits yet.</p>
    {:else}
      <ul class="log">
        {#each commits as c (c.sha)}
          <li>
            <code class="sha">{c.sha.slice(0, 7)}</code>
            <span class="msg">{firstLine(c.message)}</span>
            <span class="meta-info">{c.author} · {shortDate(c.date)}</span>
          </li>
        {/each}
      </ul>
    {/if}
  </section>
</div>

<style>
  .scm { display: flex; flex-direction: column; gap: var(--space-3); font-family: var(--type-font); border: 0.5px solid var(--border); border-radius: var(--radius-md); padding: var(--space-3) var(--space-4); background: var(--surface-sunken); }
  .head { display: flex; align-items: center; gap: var(--space-3); }
  .head h2 { margin: 0; font-size: 14px; font-weight: 600; color: var(--text-primary); }
  .branch { font-family: var(--type-mono); font-size: 11.5px; color: var(--text-secondary); background: var(--bg-active); border: 0.5px solid var(--border); border-radius: var(--radius-sm); padding: 2px 8px; }
  .spacer { flex: 1; }
  .ghost { appearance: none; background: transparent; border: 0.5px solid var(--border); border-radius: var(--radius-sm); color: var(--text-secondary); font: inherit; font-size: 11.5px; padding: 4px var(--space-2); cursor: pointer; }
  .ghost:hover:not(:disabled) { color: var(--text-primary); border-color: var(--border-strong); }
  .ghost:disabled { opacity: 0.45; cursor: default; }
  .err { margin: 0; font-size: 12px; color: var(--error); }
  .muted { margin: 0; font-size: 12.5px; color: var(--text-tertiary); line-height: 1.5; }
  .muted.small { font-size: 11.5px; padding: var(--space-2) var(--space-3); }

  .block { display: flex; flex-direction: column; gap: var(--space-2); }
  .block-head { display: flex; align-items: center; gap: var(--space-2); font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-tertiary); }
  .count { font-family: var(--type-mono); color: var(--text-secondary); background: var(--bg-active); border-radius: var(--radius-sm); padding: 0 6px; font-size: 10.5px; }

  .changes { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; border: 0.5px solid var(--border-subtle); border-radius: var(--radius-sm); overflow: hidden; }
  .chg { width: 100%; appearance: none; background: transparent; border: 0; border-bottom: 0.5px solid var(--border-subtle); display: flex; align-items: center; gap: var(--space-2); padding: 6px var(--space-2); cursor: pointer; text-align: left; }
  .chg:hover, .chg.on { background: var(--bg-active); }
  .badge { font-family: var(--type-mono); font-size: 10.5px; width: 16px; text-align: center; border-radius: 3px; flex-shrink: 0; }
  .badge.added { color: var(--success); }
  .badge.modified { color: var(--accent); }
  .badge.deleted { color: var(--error); }
  .path { font-family: var(--type-mono); font-size: 12px; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .chg.on .path { color: var(--text-primary); }
  .diffrow { background: var(--surface-base); border-bottom: 0.5px solid var(--border-subtle); }
  .diff { max-height: 280px; overflow: auto; padding: var(--space-2) 0; font-family: var(--type-mono); font-size: 11.5px; line-height: 1.5; }
  .dl { display: block; padding: 0 var(--space-3); white-space: pre; color: var(--text-secondary); border-left: 2px solid transparent; }
  .dl.add { color: var(--success); border-left-color: var(--success); }
  .dl.del { color: var(--error); border-left-color: var(--error); }
  .dl.hunk { color: var(--accent); }
  .dl.meta { color: var(--text-tertiary); }

  .commit { display: flex; gap: var(--space-2); align-items: center; }
  .commit input { flex: 1; min-width: 0; padding: 7px var(--space-3); background: var(--surface-base); border: 0.5px solid var(--border); border-radius: var(--radius-md); color: var(--text-primary); font: inherit; font-size: 12.5px; }
  .commit input:disabled { opacity: 0.5; }
  .commit-btn { appearance: none; background: var(--bg-active); border: 0.5px solid var(--border-strong); border-radius: var(--radius-md); color: var(--text-primary); font: inherit; font-size: 12.5px; font-weight: 500; padding: 7px var(--space-4); cursor: pointer; white-space: nowrap; }
  .commit-btn:disabled { opacity: 0.45; cursor: default; }
  .note { margin: 0; font-size: 11.5px; color: var(--text-tertiary); }

  .log { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
  .log li { display: flex; align-items: baseline; gap: var(--space-2); padding: 3px 0; font-size: 12px; }
  .sha { font-family: var(--type-mono); font-size: 11px; color: var(--accent); flex-shrink: 0; }
  .msg { color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; min-width: 0; }
  .meta-info { font-size: 11px; color: var(--text-tertiary); flex-shrink: 0; }
</style>
